import React, { Component } from 'react';
import styled from 'styled-components/macro';
import { WithTranslation, withTranslation } from 'react-i18next';
import cc, { CurrencyCodeRecord } from 'currency-codes';

import { Td, Tr } from '../../../components/Table';
import { Checkbox, ValidationState, FormattedInput } from '../../../components/Forms';
import { Money } from '../../../components/Formatters';
import { LineItem, Order } from '../../../types/orders';
import { fromMonetaryAmountString, toMonetaryAmountString } from '../../../helpers/formatters';
import { OperationLineItem } from '../../types';
import { getLocaleNumeralDelimiter, getLocaleNumeralDecimal } from '../../../helpers/intl';

export interface ItemRowProps extends WithTranslation {
    item: LineItem;
    operationItem: OperationLineItem | undefined;
    checked: boolean;
    order: Order;
    onChange: (item: LineItem, amount: number) => void;
}

class ItemRow extends Component<ItemRowProps> {
    constructor(props: ItemRowProps, context: any) {
        super(props, context);
        this.toggleItem = this.toggleItem.bind(this);
        this.changeAmount = this.changeAmount.bind(this);
    }

    toggleItem = () => {
        const { item, onChange, checked } = this.props;
        onChange(item, checked ? 0 : item.net_amount || 0);
    };

    changeAmount = (value: string) => {
        const { order, item, onChange, i18n } = this.props;
        const amount = fromMonetaryAmountString(i18n.language, value, order.currency);
        onChange(item, amount);
    };

    render() {
        const { item, order, checked, i18n, operationItem } = this.props;

        const getClickableCellClassName = (className: string, disabled: boolean) => {
            if (disabled) {
                return className;
            }
            return `${className} interactive`;
        };
        const exponent = cc.code(order.currency) ? (cc.code(order.currency) as CurrencyCodeRecord).digits : 2;
        const delimiter = getLocaleNumeralDelimiter(i18n.language);
        const numeralDecimalMark = getLocaleNumeralDecimal(i18n.language);

        const amountNumber = operationItem ? operationItem.amount : 0 || 0;

        return (
            <ItemTr key={item.line_id}>
                <ItemTd className="checkbox">
                    <Checkbox
                        name={`${item.line_id || -1}`}
                        label={null}
                        checked={checked}
                        onChange={this.toggleItem}
                    />
                </ItemTd>
                <ItemTd className={getClickableCellClassName('id', false)} onClick={this.toggleItem}>
                    {item.line_id}
                </ItemTd>
                <ItemTd className={getClickableCellClassName('description', false)} onClick={this.toggleItem}>
                    {item.description}
                </ItemTd>
                <ItemTd className={getClickableCellClassName('details', false)} onClick={this.toggleItem}>
                    {item.description_alias}
                </ItemTd>
                <ItemTd className="quantity" onClick={this.toggleItem}>
                    {item.quantity || 0}
                </ItemTd>
                <ItemTd className="amount">
                    <AmountInputWrapper>
                        <FormattedInput
                            options={{
                                numeral: true,
                                numericOnly: true,
                                numeralThousandsGroupStyle: 'thousand',
                                delimiter,
                                numeralIntegerScale: `${item.net_amount}`.length,
                                numeralDecimalMark,
                                numeralPositiveOnly: true,
                                numeralDecimalScale: exponent,
                            }}
                            autoComplete="off"
                            placeholder={toMonetaryAmountString(i18n.language, 0, order.currency)}
                            type="tel"
                            inputMode="numeric"
                            name="amountToCapture"
                            value={
                                amountNumber === 0
                                    ? ''
                                    : toMonetaryAmountString(i18n.language, amountNumber, order.currency)
                            }
                            onChange={() => {}}
                            onBlur={(e: React.FormEvent<HTMLInputElement>) => {
                                this.changeAmount(e.currentTarget.value);
                            }}
                            validation={
                                (item.net_amount || 0) < amountNumber
                                    ? {
                                        state: ValidationState.Invalid,
                                        message: '',
                                    }
                                    : undefined
                            }
                            disabled={false}
                        />
                    </AmountInputWrapper>
                    {' / '}
                    <Money amount={item.net_amount || 0} currency={order.currency} />
                </ItemTd>
            </ItemTr>
        );
    }
}

export default withTranslation()<React.ComponentType<ItemRowProps>>(ItemRow);

const AmountInputWrapper = styled.span`
    width: 50%;
    display: inline-block;
    padding-top: 18px;
`;

const ItemTd = styled(Td)`
    &.amount {
        width: 20%;
    }
    &.quantity {
        width: 5%;
    }
    &.description {
        width: 25%;
        user-select: none;
    }
    &.details {
        width: 30%;
        user-select: none;
    }
    &.id {
        width: 15%;
        user-select: none;
    }
    &.checkbox {
        width: 5%;
    }
    &.interactive {
        cursor: pointer;
    }
`;

const ItemTr = styled(Tr)`
    > td {
        padding-top: 0px !important;
        padding-bottom: 0px !important;
    }
`;
